/**
 * Locale-aware formatting.
 *
 * Dates, numbers and currency follow the UI language: a German reader sees
 * 04.09.2026 and 1.234,50 €, an English one 04/09/2026 and €1,234.50. The
 * currency stays EUR in both — that is the product, not the language.
 */

import { INTL_LOCALES, type Locale } from './locale.js';
import {
  ANSWER_LABELS,
  APPROVAL_LABELS,
  LEAD_TYPE_LABELS,
  OFFER_LABELS,
  PAYMENT_FREQUENCY_LABELS,
  REVIEW_LABELS,
  ROLE_LABELS,
} from './domain.js';

/** Shown wherever a value is absent, in both languages. */
export const EMPTY_VALUE = '—';

// Intl formatters are expensive to build, so keep one per locale and kind.
const dateTimeFormatters = new Map<Locale, Intl.DateTimeFormat>();
const dateFormatters = new Map<Locale, Intl.DateTimeFormat>();
const moneyFormatters = new Map<Locale, Intl.NumberFormat>();

function dateTimeFormatter(locale: Locale): Intl.DateTimeFormat {
  let formatter = dateTimeFormatters.get(locale);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(INTL_LOCALES[locale], {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
    dateTimeFormatters.set(locale, formatter);
  }
  return formatter;
}

function dateFormatter(locale: Locale): Intl.DateTimeFormat {
  let formatter = dateFormatters.get(locale);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(INTL_LOCALES[locale], {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
    dateFormatters.set(locale, formatter);
  }
  return formatter;
}

function moneyFormatter(locale: Locale): Intl.NumberFormat {
  let formatter = moneyFormatters.get(locale);
  if (!formatter) {
    formatter = new Intl.NumberFormat(INTL_LOCALES[locale], {
      style: 'currency',
      currency: 'EUR',
    });
    moneyFormatters.set(locale, formatter);
  }
  return formatter;
}

export function formatDateTime(locale: Locale, iso: string | null | undefined): string {
  if (!iso) return EMPTY_VALUE;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? EMPTY_VALUE : dateTimeFormatter(locale).format(date);
}

export function formatDate(locale: Locale, iso: string | null | undefined): string {
  if (!iso) return EMPTY_VALUE;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? EMPTY_VALUE : dateFormatter(locale).format(date);
}

/** Amounts are stored in cents, as they are everywhere else in the platform. */
export function formatMoney(locale: Locale, cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return EMPTY_VALUE;
  return moneyFormatter(locale).format(cents / 100);
}

/**
 * Enum lookups. Each falls back to the raw value so an enum member added on the
 * server before the clients know about it still renders something.
 */
const labelLookup =
  <K extends string>(table: Record<Locale, Record<K, string>>) =>
  (locale: Locale, value: string): string =>
    (table[locale] as Record<string, string>)[value] ?? value;

export const leadTypeLabel = labelLookup(LEAD_TYPE_LABELS);
export const frequencyLabel = labelLookup(PAYMENT_FREQUENCY_LABELS);
export const approvalLabel = labelLookup(APPROVAL_LABELS);
export const reviewLabel = labelLookup(REVIEW_LABELS);
export const offerLabel = labelLookup(OFFER_LABELS);
export const roleLabel = labelLookup(ROLE_LABELS);
export const answerLabel = labelLookup(ANSWER_LABELS);
